import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * iOS-Style Wheel-Picker für Zeiten als Bottom-Sheet.
 * - 15-Min-Raster von 05:00 bis 22:45
 * - Das Rad öffnet IMMER beim aktuell gespeicherten Wert: liegt der nicht auf
 *   dem Raster (z.B. eine ungerade Stempelzeit), wird er als Anker eingefügt,
 *   statt ihn still zu verschieben (Feedback #2cdc73ad).
 * - Snap-Scrolling per CSS scroll-snap
 * - Tap aufs Eingabe-Feld öffnet, Auswahl bestätigen schließt
 *
 * ⚠️ Das Blatt hängt per Portal am `document.body`, nicht an der Stelle im
 * Baum, an der der Knopf steht. Das ist kein Schönheitsgriff:
 *
 * Stand das Feld in einem `<label>` — so wie `Zeitspanne` im Kontor-Planer es
 * tat —, dann lag das Blatt mit IM Label. Ein Label leitet jeden Klick in sich
 * an sein Bedienelement weiter: aus einem Tipp auf „Abbrechen" wurden zwei
 * Klicks, der zweite traf den Zeit-Knopf und riss das eben geschlossene Blatt
 * sofort wieder auf. Für den Nutzer sah das aus, als hinge die App — Abbrechen
 * UND Übernehmen blieben wirkungslos, ohne einen einzigen Fehler im Protokoll.
 * Am Body hängend kann kein Vorfahr mehr dazwischenfunken; nebenbei wirkt
 * `position: fixed` dort wieder gegen das Fenster statt gegen einen
 * Stapelkontext des Aufrufers.
 */

const SLOTS: string[] = (() => {
  const out: string[] = [];
  for (let h = 5; h <= 22; h++) {
    for (const m of [0, 15, 30, 45]) {
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return out;
})();

/** Raster + (falls nötig) der exakte Ist-Wert als zusätzlicher Anker, sortiert. */
function slotsWith(value: string): string[] {
  if (!value || SLOTS.includes(value)) return SLOTS;
  return [...SLOTS, value].sort();
}

const ITEM_H = 44;          // Höhe pro Slot in px
const VISIBLE = 5;          // sichtbare Slots gleichzeitig (mittiger + 2 oben + 2 unten)
const VIEWPORT_H = ITEM_H * VISIBLE;

type Props = {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  /** Optional: bevorzugter Anker (z.B. start-Zeit für end-Picker, sodass end > start ist) */
  label?: string;
};

export function MWheelTimePicker({ value, onChange, disabled, label }: Props) {
  const [open, setOpen] = useState(false);
  // Exakten Wert behalten (nicht aufs Raster snappen), nur leeres/kaputtes
  // Feld auf einen sinnvollen Default setzen.
  const safe = /^\d{2}:\d{2}$/.test(value) ? value : nearestSlot(value);
  return (
    <>
      <button
        className="m-time-input"
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(true)}
        style={{ cursor: disabled ? "default" : "pointer" }}
      >
        {safe}
      </button>
      {open && typeof document !== "undefined"
        ? createPortal(
            <WheelSheet
              initial={safe}
              label={label}
              onCancel={() => setOpen(false)}
              onConfirm={(v) => { onChange(v); setOpen(false); }}
            />,
            document.body,
          )
        : null}
    </>
  );
}

function WheelSheet({ initial, label, onCancel, onConfirm }: {
  initial: string;
  label?: string;
  onCancel: () => void;
  onConfirm: (v: string) => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const [pick, setPick] = useState(initial);
  const slots = slotsWith(initial);
  const idxInitial = Math.max(0, slots.indexOf(initial));

  // Beim Öffnen direkt auf die initiale Zeit scrollen
  useEffect(() => {
    if (scroller.current) {
      scroller.current.scrollTop = idxInitial * ITEM_H;
    }
  }, [idxInitial]);

  // Beim Scrollen: zentriertes Item ermitteln und in pick speichern
  function onScroll() {
    const el = scroller.current;
    if (!el) return;
    const idx = Math.round(el.scrollTop / ITEM_H);
    const v = slots[Math.max(0, Math.min(slots.length - 1, idx))];
    if (v && v !== pick) setPick(v);
  }

  return (
    <div className="m-sheet-backdrop" onClick={onCancel}>
      <div
        className="m-sheet"
        style={{ maxHeight: "min(80vh, 480px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="m-sheet-grab" />
        <header className="m-sheet-head">
          <div className="m-sheet-title">{label ?? "Zeit wählen"}</div>
          <div className="m-sheet-sub">In 15-Minuten-Schritten</div>
        </header>

        <div className="m-wheel-frame" style={{ height: VIEWPORT_H }}>
          {/* Auswahl-Highlight in der Mitte */}
          <div className="m-wheel-pointer" style={{ height: ITEM_H, top: (VIEWPORT_H - ITEM_H) / 2 }} />
          <div
            className="m-wheel-scroller"
            ref={scroller}
            onScroll={onScroll}
            style={{
              height: VIEWPORT_H,
              paddingTop: (VIEWPORT_H - ITEM_H) / 2,
              paddingBottom: (VIEWPORT_H - ITEM_H) / 2,
            }}
          >
            {slots.map(s => (
              <div
                key={s}
                className="m-wheel-item"
                style={{ height: ITEM_H, scrollSnapAlign: "center" }}
              >
                {s}
              </div>
            ))}
          </div>
        </div>

        <footer className="m-sheet-footer">
          <button className="m-sheet-btn is-ghost" onClick={onCancel}>Abbrechen</button>
          <button className="m-sheet-btn is-primary" onClick={() => onConfirm(pick)}>
            {pick} übernehmen
          </button>
        </footer>
      </div>
    </div>
  );
}

function nearestSlot(hm: string): string {
  if (!hm) return "09:00";
  const [hStr, mStr] = hm.split(":");
  const h = Math.max(5, Math.min(22, Number(hStr) || 9));
  const m = Number(mStr) || 0;
  const half = m < 15 ? 0 : m < 45 ? 30 : 0;
  const hAdj = m >= 45 ? h + 1 : h;
  return `${String(Math.min(22, hAdj)).padStart(2, "0")}:${String(half).padStart(2, "0")}`;
}
